// main.ts
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { ConfigProvider } from 'antd';
import { I18nextProvider } from 'react-i18next';
import '@ant-design/v5-patch-for-react-19';
import de_DE from 'antd/es/locale/de_DE';
import en_US from 'antd/es/locale/en_US';

import './index.css';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import i18n from './utils/i18n';
import { loadConfig } from './runtimeConfig';
import { env } from './env';
import { ensureActiveAccount, getMsalInstance } from './services/msal';
import { getCurrentReturnPath, getSafeReturnPath } from './utils/returnPath';

const queryClient = new QueryClient();

(async () => {
  const rootEl = document.getElementById('root')!;
  rootEl.innerHTML = '<div style="padding:16px;font:14px/1.4 system-ui">Loading…</div>';

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__APP_CONFIG__ = await loadConfig();

    const msalInstance = await getMsalInstance();

    const redirectResult = await msalInstance.handleRedirectPromise().catch(() => null);
    if (redirectResult?.account) {
      msalInstance.setActiveAccount(redirectResult.account);

      // Restore the deeplink the user originally opened before the login redirect
      const returnPath = getSafeReturnPath(redirectResult.state);
      if (returnPath) window.history.replaceState(null, '', returnPath);
    } else {
      await ensureActiveAccount();
    }

    // Optional: keep account state synced across tabs
    msalInstance.enableAccountStorageEvents?.();

    createRoot(rootEl).render(
      <StrictMode>
        <MsalProvider instance={msalInstance}>
          <I18nextProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
              <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={{
                  scopes: [env.CLIENT_SCOPE],
                  state: getCurrentReturnPath(),
                }}
              >
                <AuthProvider>
                  <ConfigProvider
                    locale={i18n.language === 'en' ? en_US : de_DE}
                    theme={{
                      token: {
                        colorPrimary: '#98cb00',
                        controlOutline: 'transparent',
                        controlOutlineWidth: 0,
                      },
                      components: {
                        Tree: {
                          directoryNodeSelectedBg: 'none',
                          nodeHoverBg: 'none',
                          directoryNodeSelectedColor: '#a0c814',
                          nodeHoverColor: '#a0c814',
                        },
                      },
                    }}
                  >
                    <App />
                  </ConfigProvider>
                </AuthProvider>
              </MsalAuthenticationTemplate>
            </QueryClientProvider>
          </I18nextProvider>
        </MsalProvider>
      </StrictMode>,
    );
  } catch (err) {
    console.error(err);
    rootEl.innerHTML = `<pre style="white-space:pre-wrap;color:red">${(err as Error).message}</pre>`;
  }
})();
